import React, { useMemo } from 'react';
import { View, ScrollView, ActivityIndicator, Text, StyleSheet, Platform } from 'react-native';
import DLOButton from '../../../components/DLOButton';
import DLOCreateScheduleTableHeader from '../../../components/Tables/DLOCreateScheduleTableHeader';
import DLOCreateScheduleTableContent from '../../../components/Tables/DLOCreateScheduleTableContent';
import DLOCreateActualHoursTableContent from '../../../components/Tables/DLOCreateActualHoursTableContent';
import DLOCreateScheduleTableActions from '../../../components/Tables/DLOCreateScheduleTableActions';
import DLOCreateScheduleTableActionsHeader from '../../../components/Tables/DLOCreateScheduleTableActionsHeader';
import DLOCreateActualHoursTableActionsHeader from '../../../components/Tables/DLOCreateActualHoursTableActionsHeader';
import DLOCreateScheduleEmployeeRow, { EmployeeRowData, hasIncompleteEmployeeRows } from '../../../components/Tables/DLOCreateScheduleEmployeeRow';
import DLOCreateActualHoursEmployeeRow, { ActualHoursEmployeeRowData } from '../../../components/Tables/DLOCreateActualHoursEmployeeRow';
import DLOCreateScheduleSplitRoleRow from '../../../components/Tables/DLOCreateScheduleSplitRoleRow';
import DLOCreateScheduleActualSplitRoleRow from '../../../components/Tables/DLOCreateScheduleActualSplitRoleRow';
import { DLODropdownOption } from '../../../types/dlo.types';
import { RolesByType } from '../../../components/DLOFilterByRoleModal';
import { DayItem } from '../../../components/DLOFilterByDayModal';
import { ApiDataTransformed } from '../types';
import DLOCreateScheduleTableActualHeader from '../../../components/Tables/DLOCreateScheduleTableHeaderActual';

/**
 * Props for the DLOCreateScheduleTable component
 */
interface DLOCreateScheduleTableProps {
  // Data
  tableData: ApiDataTransformed[];
  actualTableData: ApiDataTransformed[];
  employeeRows: EmployeeRowData[];
  actualEmployeeRows: EmployeeRowData[];
  employeeOptions: DLODropdownOption[];
  roleOptions: DLODropdownOption[];
  shiftOptions: DLODropdownOption[];
  mergedRolesWithFilterState: RolesByType;
  selectedDays: string[];
  actualSelectedDays: string[];
  dayFilter: DayItem[];
  actualDayFilter: DayItem[];
  filteredRoles: RolesByType;
  actualFilteredRoles: RolesByType;

  // Loading states
  scheduleHoursLoading: boolean;
  scheduleEmployeeDataLoading: boolean;
  employeesLoading: boolean;
  employeeRolesLoading: boolean;
  shiftTimesLoading: boolean;

  // UI state
  activeTab: 'schedule' | 'actual';
  hasData: boolean;
  canSplitRole: boolean; // Add canSplitRole prop
  
  // Disabled states for week cutoff logic
  scheduledEditable: boolean;
  actualEditable: boolean;
  scheduledInputsEnabled: boolean;
  actualInputsEnabled: boolean;
  scheduledDayFiltersEnabled: boolean;
  actualDayFiltersEnabled: boolean;

  // Dropdown management
  isDropdownOpen: (rowId: string, type: 'employee' | 'role' | 'shift') => boolean;
  handleGlobalDropdownOpen: (rowId: string, type: 'employee' | 'role' | 'shift', isOpen: boolean) => void;

  // Event handlers
  handleAddEmployeeRow: () => void;
  handleAddActualEmployeeRow: () => void;
  handleTabToggle: (type: 'schedule' | 'actual') => void;
  handleEmployeeRowUpdate: (id: string, updates: any) => void;
  handleActualEmployeeRowUpdate: (id: string, updates: any) => void;
  handleEmployeeRowDelete: (id: string) => void;
  handleActualEmployeeRowDelete: (id: string) => void;
  handleSplitRowUpdate: (id: string, updates: any) => void;
  handleSplitRole: () => void;
  handleActualSplitRole: () => void;
  handleDeleteSelectedRows: () => void;
  handleDeleteSelectedActualRows: () => void;
  handleSplitRoleDelete: (childRowId: string) => void;
  handleActualSplitRoleDelete: (childRowId: string) => void;
  handleMassUpdate: (hours: string) => void;
  handleActualMassUpdate: (hours: string) => void;
  handleSelectAllEmployees: () => void;
  handleSelectAllActualEmployees: () => void;
  handleRoleFilterSave: (selectedRoles: RolesByType) => void;
  handleActualRoleFilterSave: (selectedRoles: RolesByType) => void;
  handleDayFilterSave: (filteredDays: DayItem[]) => void;
  handleActualDayFilterSave: (filteredDays: DayItem[]) => void;
  handleDayFilterPress: (day: string) => void;
  handleActualDayFilterPress: (day: string) => void;
  handleEmployeeRowUpdateMultiple: (updates: Array<{ id: string; updatedData: Partial<EmployeeRowData> }>) => void;
  handleActualEmployeeRowUpdateMultiple: (updates: Array<{ id: string; updatedData: Partial<EmployeeRowData> }>) => void;
  
  // Save functionality
  handleUniversalSave: () => void;
  saveLoading: boolean;
  hasUnsavedChanges: boolean;
}

/**
 * Main table component for DLO Create Schedule
 * Combines table header, content, actions, and employee rows
 */
const DLOCreateScheduleTable: React.FC<DLOCreateScheduleTableProps> = ({
  tableData,
  actualTableData,
  employeeRows,
  actualEmployeeRows,
  employeeOptions,
  roleOptions,
  shiftOptions,
  mergedRolesWithFilterState,
  selectedDays,
  actualSelectedDays,
  dayFilter,
  actualDayFilter,
  filteredRoles,
  actualFilteredRoles,
  scheduleHoursLoading,
  scheduleEmployeeDataLoading,
  employeesLoading,
  employeeRolesLoading,
  shiftTimesLoading,
  activeTab,
  hasData,
  canSplitRole, // Add canSplitRole to destructuring
  scheduledEditable,
  actualEditable,
  scheduledInputsEnabled,
  actualInputsEnabled,
  scheduledDayFiltersEnabled,
  actualDayFiltersEnabled,
  isDropdownOpen,
  handleGlobalDropdownOpen,
  handleAddEmployeeRow,
  handleAddActualEmployeeRow,
  handleTabToggle,
  handleEmployeeRowUpdate,
  handleActualEmployeeRowUpdate,
  handleEmployeeRowDelete,
  handleActualEmployeeRowDelete,
  handleSplitRowUpdate,
  handleSplitRole,
  handleActualSplitRole,
  handleDeleteSelectedRows,
  handleDeleteSelectedActualRows,
  handleSplitRoleDelete,
  handleActualSplitRoleDelete,
  handleMassUpdate,
  handleActualMassUpdate,
  handleSelectAllEmployees,
  handleSelectAllActualEmployees,
  handleRoleFilterSave,
  handleActualRoleFilterSave,
  handleDayFilterSave,
  handleActualDayFilterSave,
  handleDayFilterPress,
  handleActualDayFilterPress,
  handleEmployeeRowUpdateMultiple,
  handleActualEmployeeRowUpdateMultiple,
  handleUniversalSave,
  saveLoading,
  hasUnsavedChanges
}) => {
  // Helper function to check if an employee row should be displayed based on role filter
  const isEmployeeRowVisible = (
    employeeRow: EmployeeRowData,
    currentFilteredRoles: RolesByType
  ): boolean => {
    // If no role filter is set or employee has no role, show the row
    if (!currentFilteredRoles || !employeeRow.role?.label) {
      return true;
    }

    // Get all roles from filtered roles with their enabled state
    const allFilteredRoles = [
      ...currentFilteredRoles.DSD,
      ...currentFilteredRoles['NON-DSD'],
      ...currentFilteredRoles.Retail,
      ...currentFilteredRoles.Other,
    ];

    // Find matching role by name (case insensitive, simplified comparison)
    const matchingRole = allFilteredRoles.find((role) => {
      const simplifiedFilterRoleName = role.name.toLowerCase().trim();
      const simplifiedEmployeeRoleName = employeeRow
        .role!.label.toLowerCase()
        .trim();
      
      // Direct match
      if (simplifiedFilterRoleName === simplifiedEmployeeRoleName) {
        return true;
      }
      
      // Partial match (in case of role name variations)
      if (
        simplifiedFilterRoleName.includes(simplifiedEmployeeRoleName) ||
        simplifiedEmployeeRoleName.includes(simplifiedFilterRoleName)
      ) {
        return true;
      }
      
      return false;
    });

    // If no matching role found in filter, show the row (default behavior)
    // If matching role found, show only if it's enabled
    return matchingRole ? matchingRole.enabled : true;
  };

  // Create filtered employee rows based on role filter
  const filteredEmployeeRows = employeeRows.filter((employeeRow: EmployeeRowData) => 
    isEmployeeRowVisible(employeeRow, filteredRoles)
  );
  
  const filteredActualEmployeeRows = actualEmployeeRows.filter((employeeRow: EmployeeRowData) => 
    isEmployeeRowVisible(employeeRow, actualFilteredRoles)
  );

  // Helper function to get available employee options for a specific row
  const getAvailableEmployeeOptions = (currentRowId: string, isActualTab: boolean) => {
    const relevantRows = isActualTab ? actualEmployeeRows : employeeRows;
    
    // Find the current row to understand its split relationship
    const currentRow = relevantRows.find((row) => row.id === currentRowId);
    
    // Get all selected employee IDs except for:
    // 1. The current row
    // 2. Rows that are part of the same split group (same parent or same child group)
    const selectedEmployeeIds = relevantRows
      .filter((row: EmployeeRowData) => {
        // Don't exclude the current row
        if (row.id === currentRowId) {
          return false;
        }
        
        // Don't exclude if they're part of the same split group
        if (currentRow && currentRow.isChildRow && row.isChildRow) {
          // Both are child rows - check if they have the same parent
          if (currentRow.parentId === row.parentId) {
            return false;
          }
        } else if (currentRow && !currentRow.isChildRow && currentRow.isSplit) {
          // Current row is a parent with split - allow child rows of this parent
          if (row.isChildRow && row.parentId === currentRow.id) {
            return false;
          }
        } else if (currentRow && currentRow.isChildRow && !row.isChildRow) {
          // Current row is child, other row is parent - check if it's the parent of current row
          if (currentRow.parentId === row.id) {
            return false;
          }
        }
        
        // Include this row in the exclusion list
        return row.employee?.id;
      })
      .map((row: EmployeeRowData) => row.employee?.id)
      .filter(Boolean);
    
    // Filter out already selected employees (except those in the same split group)
    return employeeOptions.filter((option) => !selectedEmployeeIds.includes(option.id));
  };

  // Helper function to get available role options based on current role filter
  const getAvailableRoleOptions = (isActualTab: boolean, parentRow?: EmployeeRowData, currentRowId?: string) => {
    const currentFilteredRoles = isActualTab ? actualFilteredRoles : filteredRoles;
    
    // Get initial role options based on filter
    let availableRoles = roleOptions;
    
    // If filter is applied, filter role options first
    if (currentFilteredRoles) {
      // Get all roles from filtered roles with their enabled state
      const allFilteredRoles = [
        ...currentFilteredRoles.DSD,
        ...currentFilteredRoles['NON-DSD'],
        ...currentFilteredRoles.Retail,
        ...currentFilteredRoles.Other,
      ];

      // Filter role options to only include roles that are enabled in the filter
      availableRoles = roleOptions.filter((roleOption) => {
        // Find matching role by name (case insensitive, simplified comparison)
        const matchingRole = allFilteredRoles.find((role) => {
          const simplifiedFilterRoleName = role.name.toLowerCase().trim();
          const simplifiedRoleOptionName = roleOption.label.toLowerCase().trim();
          
          // Direct match
          if (simplifiedFilterRoleName === simplifiedRoleOptionName) {
            return true;
          }
          
          // Partial match (in case of role name variations)
          if (
            simplifiedFilterRoleName.includes(simplifiedRoleOptionName) ||
            simplifiedRoleOptionName.includes(simplifiedFilterRoleName)
          ) {
            return true;
          }
          
          return false;
        });

        // If no matching role found in filter, include it (default behavior)
        // If matching role found, include only if it's enabled
        return matchingRole ? matchingRole.enabled : true;
      });
    }

    // For split role rows, we no longer exclude the parent role
    // The validation is now done at the role+shift combination level in getAvailableShiftOptions

    return availableRoles;
  };

  // Helper function to get available shift options that prevents duplicate role+shift combinations
  const getAvailableShiftOptions = (isActualTab: boolean, parentRow?: EmployeeRowData, currentRowId?: string, selectedRole?: DLODropdownOption | null) => {
    const relevantRows = isActualTab ? actualEmployeeRows : employeeRows;
    
    // If no parent row or no current role selected, return all shift options
    if (!parentRow || !selectedRole) {
      return shiftOptions;
    }

    // Get the selected role ID to check for combinations
    const selectedRoleId = selectedRole.id || selectedRole.value;
    
    // Collect all role+shift combinations that should be excluded
    const excludedCombinations = new Set<string>();
    
    // Add parent row's role+shift combination
    if (parentRow.role && parentRow.shift) {
      const parentRoleId = parentRow.role.id || parentRow.role.value;
      const parentShiftId = parentRow.shift.id || parentRow.shift.value;
      excludedCombinations.add(`${parentRoleId}_${parentShiftId}`);
    }
    
    // If we have a currentRowId, find all sibling child rows and collect their role+shift combinations
    if (currentRowId) {
      const currentRow = relevantRows.find(row => row.id === currentRowId);
      
      if (currentRow && currentRow.isChildRow && currentRow.parentId) {
        // Find all child rows with the same parent (siblings)
        const siblingRows = relevantRows.filter(row => 
          row.isChildRow && 
          row.parentId === currentRow.parentId && 
          row.id !== currentRowId && // Exclude current row
          row.role && 
          row.shift // Only consider rows that have both role and shift assigned
        );
        
        // Add all sibling role+shift combinations to exclusion set
        siblingRows.forEach(siblingRow => {
          if (siblingRow.role && siblingRow.shift) {
            const siblingRoleId = siblingRow.role.id || siblingRow.role.value;
            const siblingShiftId = siblingRow.shift.id || siblingRow.shift.value;
            excludedCombinations.add(`${siblingRoleId}_${siblingShiftId}`);
          }
        });
      }
    }
    
    // Filter out shifts that would create a duplicate role+shift combination
    return shiftOptions.filter((shiftOption) => {
      const shiftId = shiftOption.id || shiftOption.value;
      const combination = `${selectedRoleId}_${shiftId}`;
      return !excludedCombinations.has(combination);
    });
  };

  // Determine disabled days for Actual Hours tab if current week
  const disabledDaysActual = useMemo(() => {
    if (activeTab !== 'actual' || !actualInputsEnabled) return [];
    const allDays = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
    const todayIdx = new Date().getDay();
    // Disable all days before today for all rows
    let disabled = allDays.slice(0, todayIdx);
    // Also check split role rows in actualEmployeeRows
    (actualEmployeeRows as ActualHoursEmployeeRowData[]).forEach(row => {
      // If split role parent, also check its child rows
      if (row.isSplit && !row.isChildRow) {
        // Disable past days for split parent
        allDays.slice(0, todayIdx).forEach(day => {
          if (!disabled.includes(day)) disabled.push(day);
        });
        // Disable past days for all child rows
        const childRows = (actualEmployeeRows as ActualHoursEmployeeRowData[]).filter(r => r.parentId === row.id && r.isChildRow);
        childRows.forEach(child => {
          allDays.slice(0, todayIdx).forEach(day => {
            if (!disabled.includes(day)) disabled.push(day);
          });
        });
      }
    });
    return [...new Set(disabled)];
  }, [activeTab, actualInputsEnabled, actualEmployeeRows]);

  // Memoize role options for each tab so DropDownPicker Picker doesn't get a new
  // array reference every render (which would trigger its internal useEffect →
  // setState → infinite loop).
  const memoizedScheduleRoleOptions = useMemo(
    () => getAvailableRoleOptions(false),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredRoles, roleOptions]
  );

  const memoizedActualRoleOptions = useMemo(
    () => getAvailableRoleOptions(true),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [actualFilteredRoles, roleOptions]
  );

  // Helper function to render employee data loading state
  const renderEmployeeDataLoading = () => (
    <View style={styles.loadingContainer}>
      <ActivityIndicator size="large" color="#0066CC" />
      <Text style={styles.loadingText}>
        Loading employee data...
      </Text>
      <Text style={styles.loadingSubText}>
        Fetching existing schedule data for employees
      </Text>
    </View>
  );

  return (
    <View style={styles.container}>
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={true}
        bounces={false}
        scrollEventThrottle={16}
        contentContainerStyle={styles.scrollContent}
        style={styles.horizontalScrollView}
      >
        <View style={styles.tableWrapper}>
          {activeTab === 'schedule'?(
          <DLOCreateScheduleTableHeader />):(
          <DLOCreateScheduleTableActualHeader/>
          )}
          {/* Show loading state or content based on API call status */}
          {scheduleHoursLoading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#0066CC" />
              <Text style={styles.loadingText}>
                Loading schedule hours...
              </Text>
              <Text style={styles.loadingSubText}>
                Fetching schedule data for all roles
              </Text>
            </View>
          ) : !hasData ? (
            <View style={styles.noDataContainer}>
              <Text style={styles.noDataText}>
                No schedule hours data available
              </Text>
            </View>
          ) : (
            <>
              {/* Schedule Hours Tab - Shows FCST vs SCHD */}
              {activeTab === 'schedule' && (
                <DLOCreateScheduleTableContent
                  data={tableData}
                  filteredRoles={filteredRoles}
                />
              )}
              
              {/* Actual Hours Tab - Shows EST vs ACTUAL */}
              {activeTab === 'actual' && (
                <DLOCreateActualHoursTableContent
                  data={actualTableData}
                  filteredRoles={actualFilteredRoles}
                />
              )}
            </>
          )}

          {/* Tab Actions - Always visible regardless of active tab */}
          <DLOCreateScheduleTableActions
            onAddEmployeeRow={activeTab === 'schedule' ? handleAddEmployeeRow : handleAddActualEmployeeRow}
            onSplitRole={activeTab === 'schedule' ? handleSplitRole : handleActualSplitRole}
            onDeleteRows={activeTab === 'schedule' ? handleDeleteSelectedRows : handleDeleteSelectedActualRows}
            onMassUpdate={activeTab === 'schedule' ? handleMassUpdate : handleActualMassUpdate}
            onFilterByRole={() => console.log('Filter by Role pressed')}
            onFilterByDay={() => console.log('Filter by Day pressed')}
            onToggleHours={handleTabToggle}
            activeTab={activeTab}
            rolesByType={mergedRolesWithFilterState}
            onRoleFilterSave={activeTab === 'schedule' ? handleRoleFilterSave : handleActualRoleFilterSave}
            onDayFilterSave={activeTab === 'schedule' ? handleDayFilterSave : handleActualDayFilterSave}
            hasSelectedEmployees={activeTab === 'schedule' 
              ? filteredEmployeeRows.some((row: EmployeeRowData) => row.isSelected)
              : filteredActualEmployeeRows.some((row: EmployeeRowData) => row.isSelected)
            }
            selectedEmployeesCount={activeTab === 'schedule'
              ? filteredEmployeeRows.filter((row: EmployeeRowData) => row.isSelected).length
              : filteredActualEmployeeRows.filter((row: EmployeeRowData) => row.isSelected).length
            }
            canSplitRole={activeTab === 'schedule' ? (canSplitRole && scheduledEditable) : (canSplitRole && actualEditable)}
            selectedDays={activeTab === 'schedule' ? selectedDays : actualSelectedDays}
            dayFilterData={activeTab === 'schedule' ? dayFilter : actualDayFilter}
            isAddEmployeeDisabled={scheduleEmployeeDataLoading || (activeTab === 'schedule' ? !scheduledEditable : !actualEditable)}
            isSplitRoleDisabled={activeTab === 'schedule' ? !scheduledEditable : !actualEditable}
            isDeleteDisabled={activeTab === 'schedule' ? !scheduledEditable : !actualEditable}
            isMassUpdateDisabled={activeTab === 'schedule' ? !scheduledEditable : !actualEditable}
            isFilterByDayDisabled={activeTab === 'schedule' ? !scheduledDayFiltersEnabled : !actualDayFiltersEnabled}
            scheduledEditable={scheduledEditable}
            actualEditable={actualEditable}
          />

          {/* Show table actions header and employee rows only for Schedule Hours tab */}
          {activeTab === 'schedule' && (
            <>
              <DLOCreateScheduleTableActionsHeader
                isChecked={filteredEmployeeRows.length > 0 && filteredEmployeeRows.every((row: EmployeeRowData) => row.isSelected)}
                onCheckboxPress={handleSelectAllEmployees}
                selectedDays={selectedDays}
                onDayFilterPress={handleDayFilterPress}
                dayFilter={dayFilter}
                inputsEnabled={scheduledInputsEnabled}
              />

              {/* Employee Rows - Show loading or data */}
              {scheduleEmployeeDataLoading ? (
                renderEmployeeDataLoading()
              ) : (
                filteredEmployeeRows.map((employeeRow: EmployeeRowData) => {
                // If this is a child row, render the split role component
                if (employeeRow.isChildRow) {
                  // Find the parent row for this child row
                  const parentRow = employeeRows.find((row: EmployeeRowData) =>
                    row.id === employeeRow.parentId && !row.isChildRow
                  );

                  return (
                    <DLOCreateScheduleSplitRoleRow
                      key={employeeRow.id}
                      data={employeeRow}
                      parentData={parentRow}
                      onUpdate={handleSplitRowUpdate}
                      onDelete={handleSplitRoleDelete}
                      roleOptions={getAvailableRoleOptions(false, parentRow, employeeRow.id)}
                      shiftOptions={getAvailableShiftOptions(false, parentRow, employeeRow.id, employeeRow.role)}
                      selectedDays={selectedDays}
                      dayFilter={dayFilter}
                      // Global dropdown management
                      isRoleDropdownOpen={isDropdownOpen(employeeRow.id, 'role')}
                      isShiftDropdownOpen={isDropdownOpen(employeeRow.id, 'shift')}
                      onDropdownOpen={handleGlobalDropdownOpen}
                      // Loading states
                      isRoleLoading={employeeRolesLoading}
                      isShiftLoading={shiftTimesLoading}
                      // Input field enable/disable based on week cutoff
                      inputsEnabled={scheduledInputsEnabled}
                    />
                  );
                }

                // Otherwise, render the regular employee row
                return (
                  <DLOCreateScheduleEmployeeRow
                    key={employeeRow.id}
                    data={employeeRow}
                    onUpdate={handleEmployeeRowUpdate}
                    onDelete={handleEmployeeRowDelete}
                    employeeOptions={getAvailableEmployeeOptions(employeeRow.id, false)}
                    roleOptions={memoizedScheduleRoleOptions}
                    shiftOptions={shiftOptions}
                    selectedDays={selectedDays}
                    dayFilter={dayFilter}
                    // All employee rows for calculating totals including child split rows
                    allEmployeeRows={employeeRows}
                    // Function to update multiple rows (for parent checkbox selecting all children)
                    onUpdateMultiple={handleEmployeeRowUpdateMultiple}
                    // Global dropdown management
                    isEmployeeDropdownOpen={isDropdownOpen(employeeRow.id, 'employee')}
                    isRoleDropdownOpen={isDropdownOpen(employeeRow.id, 'role')}
                    isShiftDropdownOpen={isDropdownOpen(employeeRow.id, 'shift')}
                    onDropdownOpen={handleGlobalDropdownOpen}
                    // Input field enable/disable based on week cutoff
                    inputsEnabled={scheduledInputsEnabled}
                    // Loading states
                    isEmployeeLoading={employeesLoading}
                    isRoleLoading={employeeRolesLoading}
                    isShiftLoading={shiftTimesLoading}
                    // Validation callbacks to track completion state
                    onValidationChange={() => {}}
                  />
                );
                })
              )}
            </>
          )}

          {/* Actual Hours tab with dedicated employee rows */}
          {activeTab === 'actual' && (
            <>
              <DLOCreateActualHoursTableActionsHeader
                isChecked={filteredActualEmployeeRows.length > 0 && filteredActualEmployeeRows.every((row: EmployeeRowData) => row.isSelected)}
                onCheckboxPress={handleSelectAllActualEmployees}
                selectedDays={actualSelectedDays}
                onDayFilterPress={handleActualDayFilterPress}
                dayFilter={actualDayFilter}
                inputsEnabled={actualInputsEnabled}
                disabledDays={disabledDaysActual}
              />

              {/* Actual Hours Employee Rows - Show loading or data */}
              {scheduleEmployeeDataLoading ? (
                renderEmployeeDataLoading()
              ) : (
                 filteredActualEmployeeRows.map((employeeRow) => {
                // If this is a child row, render the actual split role component
                if (employeeRow.isChildRow) {
                  // Find the parent row for this child row
                  const parentRow = actualEmployeeRows.find((row: EmployeeRowData) =>
                    row.id === employeeRow.parentId && !row.isChildRow
                  );

                  return (
                    <DLOCreateScheduleActualSplitRoleRow
                      key={employeeRow.id}
                      data={employeeRow}
                      parentData={parentRow}
                      onUpdate={handleActualEmployeeRowUpdate}
                      onDelete={handleActualSplitRoleDelete}
                      roleOptions={getAvailableRoleOptions(true, parentRow, employeeRow.id)}
                      shiftOptions={getAvailableShiftOptions(true, parentRow, employeeRow.id, employeeRow.role)}
                      selectedDays={actualSelectedDays}
                      dayFilter={actualDayFilter}
                      // Global dropdown management
                      isRoleDropdownOpen={isDropdownOpen(employeeRow.id, 'role')}
                      isShiftDropdownOpen={isDropdownOpen(employeeRow.id, 'shift')}
                      onDropdownOpen={handleGlobalDropdownOpen}
                      // Loading states
                      isRoleLoading={employeeRolesLoading}
                      isShiftLoading={shiftTimesLoading}
                      // Schedule data for variance calculation
                      scheduleEmployeeRows={employeeRows}
                      // Input control
                      inputsEnabled={actualInputsEnabled}
                      // <-- Pass disabledDaysActual to split role row
                      disabledDays={disabledDaysActual}
                    />
                  );
                }

                // Otherwise, render the regular actual hours employee row
                return (
                  <DLOCreateActualHoursEmployeeRow
                    key={employeeRow.id}
                    data={employeeRow as ActualHoursEmployeeRowData}
                    onUpdate={handleActualEmployeeRowUpdate}
                    onDelete={handleActualEmployeeRowDelete}
                    employeeOptions={getAvailableEmployeeOptions(employeeRow.id, true)}
                    roleOptions={memoizedActualRoleOptions}
                    shiftOptions={shiftOptions}
                    selectedDays={actualSelectedDays}
                    dayFilter={actualDayFilter}
                    // All employee rows for calculating totals including child split rows
                    allEmployeeRows={actualEmployeeRows as ActualHoursEmployeeRowData[]}
                    // Function to update multiple rows (for parent checkbox selecting all children)
                    onUpdateMultiple={handleActualEmployeeRowUpdateMultiple}
                    // Global dropdown management
                    isEmployeeDropdownOpen={isDropdownOpen(employeeRow.id, 'employee')}
                    isRoleDropdownOpen={isDropdownOpen(employeeRow.id, 'role')}
                    isShiftDropdownOpen={isDropdownOpen(employeeRow.id, 'shift')}
                    onDropdownOpen={handleGlobalDropdownOpen}
                    // Loading states
                    isEmployeeLoading={employeesLoading}
                    isRoleLoading={employeeRolesLoading}
                    isShiftLoading={shiftTimesLoading}
                    // Input field enable/disable based on week cutoff
                    inputsEnabled={actualInputsEnabled}
                    // Schedule data for variance calculation
                    scheduleEmployeeRows={employeeRows}
                    disabledDays={disabledDaysActual}
                  />
                );
                })
              )}
            </>
          )}
        </View>
      </ScrollView>
      
      {/* Save Button positioned at bottom right */}
      <View style={styles.saveButtonContainer}>
        <DLOButton
          text="Save"
          variant="primary"
          size="medium"
          style={styles.saveButton}
          isLoading={saveLoading}
          disabled={
            saveLoading || 
            scheduleEmployeeDataLoading || 
            !hasUnsavedChanges ||
            (activeTab === 'schedule' && !scheduledEditable) ||
            (activeTab === 'actual' && !actualEditable) ||
            hasIncompleteEmployeeRows(activeTab === 'schedule' ? employeeRows : actualEmployeeRows)
          }
          onPress={handleUniversalSave}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    marginBottom: 24,
  },
  horizontalScrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    marginBottom: 8,
    justifyContent: 'center',
  },
  tableWrapper: {
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#FBFCFE',
    minWidth: 1200, // Ensure minimum width for horizontal scrolling
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
    minHeight: 200,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderLeftColor: '#DFDFDF',
    borderRightColor: '#DFDFDF',
    backgroundColor: '#FFFFFF',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  loadingSubText: {
    marginTop: 8,
    fontSize: 14,
    color: '#999',
    textAlign: 'center',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
    minHeight: 200,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderLeftColor: '#DFDFDF',
    borderRightColor: '#DFDFDF',
    backgroundColor: '#FFFFFF',
  },
  noDataText: {
    fontSize: 18,
    color: '#666',
    textAlign: 'center',
    fontWeight: '600',
    marginBottom: 8,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-SemiBold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  saveButtonContainer: {
    width: '100%',
    marginTop: 16,
    paddingRight: 18,
    paddingLeft: 18,
    zIndex: 1000,
  },
  saveButton: {
    width: 80,
    alignSelf: 'flex-end',
  }
});

export default DLOCreateScheduleTable;